import React, { useState, useEffect } from 'react';
import { Search, Menu, X, Home, Users, Vote, Building2, Info, CheckCircle } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { PeriodSelector } from '../common/PeriodSelector';

interface NavLinkProps {
  to: string;
  children: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
  icon?: React.ReactNode;
}

const NavLink: React.FC<NavLinkProps> = ({ to, children, active, onClick, icon }) => {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`
        flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-all duration-200
        ${active
          ? 'text-blue-700 bg-blue-50'
          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
        }
      `}
    >
      {active && icon}
      {children}
    </Link>
  );
};

export const CompactHeader: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Close mobile menu on route change
    setMobileMenuOpen(false);
    setSearchOpen(false);
  }, [location]);

  const isActive = (path: string) => {
    if (path === '/') {return location.pathname === '/';}
    return location.pathname.startsWith(path);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      const query = searchQuery.trim();
      const lowerQuery = query.toLowerCase();

      // Smart routing based on query content
      // Check for party names FIRST (more specific)
      const parties = ['udi', 'rn', 'evopoli', 'republicanos', 'ps', 'ppd', 'pr', 'dc',
                      'pc', 'frvs', 'comunes', 'cs', 'pdg', 'pli', 'ind'];
      const foundParty = parties.find(party => {
        // Exact match or as a word boundary (not part of another word)
        const regex = new RegExp(`\\b${party}\\b`, 'i');
        return regex.test(query);
      });
      if (foundParty) {
        navigate(`/partidos?party=${encodeURIComponent(foundParty)}`);
        setSearchQuery('');
        setSearchOpen(false);
        return;
      }

      // Known communes in Chile
      const communes = ['las condes', 'providencia', 'santiago', 'vitacura', 'la florida',
                       'puente alto', 'maipú', 'ñuñoa', 'la reina', 'lo barnechea',
                       'peñalolén', 'san miguel', 'san bernardo', 'la cisterna', 'el bosque',
                       'valparaíso', 'viña del mar', 'concepción', 'temuco', 'antofagasta'];

      // Check if searching for a commune
      const foundCommune = communes.find(commune => lowerQuery.includes(commune));
      if (foundCommune) {
        navigate(`/diputados?comuna=${encodeURIComponent(foundCommune)}`);
        setSearchQuery('');
        setSearchOpen(false);
        return;
      }

      // Check if it looks like a person's name
      const words = query.split(' ');

      // Single word that looks like a name (starts with capital, 3+ chars, no numbers)
      if (words.length === 1) {
        const singleWord = words[0];
        const isLikelySingleName = /^[A-ZÁÉÍÓÚÑ][a-záéíóúñ]{2,}$/.test(singleWord);

        if (isLikelySingleName) {
          navigate(`/diputados?search=${encodeURIComponent(query)}`);
          setSearchQuery('');
          setSearchOpen(false);
          return;
        }
      }

      // Multiple words that look like a full name
      if (words.length >= 2) {
        const isLikelyFullName = words.every(word =>
          word.length >= 2 && /^[A-ZÁÉÍÓÚÑ]/i.test(word[0]),
        );

        if (isLikelyFullName) {
          navigate(`/diputados?search=${encodeURIComponent(query)}`);
          setSearchQuery('');
          setSearchOpen(false);
          return;
        }
      }

      // Keywords for votaciones/laws
      const votingKeywords = ['ley', 'proyecto', 'votacion', 'voto', 'tema', 'boletín',
                             'aprobado', 'rechazado', 'abstención', 'moción'];
      const hasVotingKeyword = votingKeywords.some(keyword => lowerQuery.includes(keyword));

      if (hasVotingKeyword) {
        navigate(`/votaciones?search=${encodeURIComponent(query)}`);
      } else {
        // Default: If we can't identify what it is, try diputados first (most common search)
        navigate(`/diputados?search=${encodeURIComponent(query)}`);
      }

      setSearchQuery('');
      setSearchOpen(false);
    }
  };

  const handleQuickSearch = (term: string, section: string) => {
    navigate(`/${section}?search=${encodeURIComponent(term)}`);
    setSearchQuery('');
    setSearchOpen(false);
  };

  // Check if we're on deputy profile page
  const isDeputyProfile = location.pathname.startsWith('/deputy/');

  return (
    <>
      <header
        className={`
          ${isDeputyProfile ? 'relative' : 'fixed top-0 left-0 right-0'} z-50 bg-white transition-all duration-300
          ${scrolled ? 'shadow-md' : 'border-b border-gray-200'}
        `}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center group">
              <img
                src="/logo512.png"
                alt="Caramba - Tu voto importa"
                className="h-14 sm:h-16 w-auto transition-transform group-hover:scale-105"
                style={{ maxWidth: '250px' }}
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-1 absolute left-1/2 -translate-x-1/2">
              <NavLink to="/" active={isActive('/')} icon={<Home className="w-4 h-4" />}>
                Inicio
              </NavLink>
              <NavLink to="/diputados" active={isActive('/diputados')} icon={<Users className="w-4 h-4" />}>
                Diputados
              </NavLink>
              <NavLink to="/votaciones" active={isActive('/votaciones')} icon={<Vote className="w-4 h-4" />}>
                Votaciones
              </NavLink>
              <NavLink to="/partidos" active={isActive('/partidos')} icon={<Building2 className="w-4 h-4" />}>
                Partidos
              </NavLink>
              <NavLink to="/promesas" active={isActive('/promesas')} icon={<CheckCircle className="w-4 h-4" />}>
                Promesas
              </NavLink>
              <NavLink to="/acerca" active={isActive('/acerca') || isActive('/transparencia')} icon={<Info className="w-4 h-4" />}>
                Acerca de
              </NavLink>
            </nav>

            {/* Actions */}
            <div className="flex items-center space-x-3">
              {/* Period Selector (desktop; the mobile menu has its own).
                  Lives in the right-hand group so it never overlaps the
                  absolutely-centered nav. */}
              <div className="hidden md:block">
                <PeriodSelector />
              </div>

              {/* Search Button */}
              <button
                onClick={() => {
                  setSearchOpen(!searchOpen);
                  setMobileMenuOpen(false);
                }}
                className="p-2 text-gray-600 hover:text-blue-700 transition-colors rounded-md hover:bg-gray-50"
                aria-label="Search"
              >
                <Search className="w-5 h-5" />
              </button>

              {/* Mobile Menu Button */}
              <button
                onClick={() => {
                  setMobileMenuOpen(!mobileMenuOpen);
                  setSearchOpen(false);
                }}
                className="md:hidden p-2 text-gray-600 hover:text-gray-900 transition-colors rounded-md hover:bg-gray-50"
                aria-label="Menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>

              {/* Chilean Flag - Moved to far right */}
              <div className="ml-2 sm:ml-4">
                <img
                  src="/assets/chilean-flag/Flag_of_Chile.svg"
                  alt="Bandera de Chile"
                  className="h-8 sm:h-10 w-auto rounded shadow-md border border-gray-300 hover:shadow-lg transition-shadow cursor-pointer"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Quick Search Dropdown */}
        <AnimatePresence>
          {searchOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="absolute top-full left-0 right-0 bg-white border-b shadow-lg"
            >
              <div className="max-w-2xl mx-auto p-4">
                <form onSubmit={handleSearch} className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Buscar nombres, partidos o leyes..."
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 placeholder:text-sm"
                    autoFocus
                  />
                  <button
                    type="submit"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                  >
                    <Search className="w-4 h-4" />
                  </button>
                </form>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="md:hidden bg-white border-b shadow-lg"
            >
              <nav className="px-4 py-3 space-y-1">
                <NavLink
                  to="/"
                  active={isActive('/')}
                  onClick={() => setMobileMenuOpen(false)}
                  icon={<Home className="w-4 h-4" />}
                >
                  Inicio
                </NavLink>
                <NavLink
                  to="/diputados"
                  active={isActive('/diputados')}
                  onClick={() => setMobileMenuOpen(false)}
                  icon={<Users className="w-4 h-4" />}
                >
                  Diputados
                </NavLink>
                <NavLink
                  to="/votaciones"
                  active={isActive('/votaciones')}
                  onClick={() => setMobileMenuOpen(false)}
                  icon={<Vote className="w-4 h-4" />}
                >
                  Votaciones
                </NavLink>
                <NavLink
                  to="/partidos"
                  active={isActive('/partidos')}
                  onClick={() => setMobileMenuOpen(false)}
                  icon={<Building2 className="w-4 h-4" />}
                >
                  Partidos
                </NavLink>
                <NavLink
                  to="/promesas"
                  active={isActive('/promesas')}
                  onClick={() => setMobileMenuOpen(false)}
                  icon={<CheckCircle className="w-4 h-4" />}
                >
                  Promesas
                </NavLink>
                <NavLink
                  to="/acerca"
                  active={isActive('/acerca') || isActive('/transparencia')}
                  onClick={() => setMobileMenuOpen(false)}
                  icon={<Info className="w-4 h-4" />}
                >
                  Acerca de
                </NavLink>
                <div className="pt-2 border-t border-gray-100">
                  <PeriodSelector />
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};